import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { MessageSquare, Send, Sparkles } from 'lucide-react'
import {
  useInbox,
  useConversationHistory,
  useSendMessage,
  useMarkConversationRead,
  useSuggestReply,
} from '../../../hooks/data/useMessages'
import { toast } from '@/ui/Toast'
import type { ConversationSummary, MessageItem } from '@/types/domain'

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

/** ConversationSummary only carries customerUserId (no denormalized name) — CrmService
 * doesn't join to a customer profile here, so we surface a short id, same as OrdersPage
 * does for raw ids elsewhere in this app. */
function customerLabel(customerUserId: string): string {
  return `Customer ${customerUserId.slice(0, 8)}`
}

function MessagesPage() {
  const inbox = useInbox()
  const [selectedId, setSelectedId] = useState<string | undefined>()
  const history = useConversationHistory(selectedId)
  const sendMutation = useSendMessage()
  const markRead = useMarkConversationRead()
  const suggestReply = useSuggestReply()
  const [draft, setDraft] = useState('')
  const [suggestionCitations, setSuggestionCitations] = useState<string[]>([])

  const selected: ConversationSummary | undefined = inbox.data?.find((c) => c.id === selectedId)

  // Deep-link support: Dashboard's Customer Activity section links here with
  // ?conversationId= so "view chat" actually opens the right conversation instead of
  // dumping the manager on an empty inbox. Waits for the inbox to load since the id needs
  // to exist in inbox.data before it can be selected.
  useEffect(() => {
    if (selectedId || !inbox.data) return
    const fromUrl = new URLSearchParams(window.location.search).get('conversationId')
    if (fromUrl && inbox.data.some((c) => c.id === fromUrl)) setSelectedId(fromUrl)
  }, [inbox.data, selectedId])

  // Switching conversations must drop any stale AI draft/citations from the previous one.
  useEffect(() => {
    setDraft('')
    setSuggestionCitations([])
  }, [selectedId])

  // Mirrors ChatWidget's convention: mark read as soon as an unread conversation is opened.
  useEffect(() => {
    if (selectedId && selected && selected.unreadCount > 0 && !markRead.isPending) {
      markRead.mutate(selectedId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, selected?.unreadCount])

  function handleSend() {
    const body = draft.trim()
    if (!body || !selectedId || sendMutation.isPending) return
    sendMutation.mutate({ conversationId: selectedId, body })
    setDraft('')
    setSuggestionCitations([])
  }

  function handleSuggestReply() {
    if (!selectedId || suggestReply.isPending) return
    suggestReply.mutate(selectedId, {
      onSuccess: (result) => {
        if (!result.suggestion) {
          toast.info('No suggestion available', result.reason)
          return
        }
        setDraft(result.suggestion)
        setSuggestionCitations(result.citations)
      },
      onError: (err) => toast.error('Could not get a suggestion', err instanceof Error ? err.message : undefined),
    })
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 lg:col-span-1">
        <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
          <h1 className="flex items-center gap-2 text-lg font-semibold text-gray-800 dark:text-white">
            <MessageSquare className="h-5 w-5" /> Messages
          </h1>
        </div>
        <div className="max-h-[70vh] overflow-y-auto">
          {inbox.isLoading ? (
            <p className="p-4 text-sm text-gray-500 dark:text-gray-400">Loading conversations…</p>
          ) : inbox.isError ? (
            <p className="p-4 text-sm text-red-600">Failed to load the inbox. Requires a logged-in session.</p>
          ) : inbox.data && inbox.data.length === 0 ? (
            <p className="p-4 text-sm text-gray-500 dark:text-gray-400">No conversations yet.</p>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {inbox.data?.map((c) => (
                <li key={c.id}>
                  <button
                    onClick={() => setSelectedId(c.id)}
                    className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors ${
                      selectedId === c.id ? 'bg-blue-50 dark:bg-gray-700/50' : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'
                    }`}
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                      {c.customerUserId.slice(0, 2).toUpperCase()}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-medium text-gray-800 dark:text-gray-100">
                          {customerLabel(c.customerUserId)}
                        </span>
                        {c.unreadCount > 0 && (
                          <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-semibold text-white">
                            {c.unreadCount}
                          </span>
                        )}
                      </span>
                      <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                        {c.lastMessagePreview || 'No messages yet'}
                      </span>
                      <span className="block text-[10px] text-gray-400 dark:text-gray-500">{formatTime(c.lastMessageAt)}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="flex flex-col rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 lg:col-span-2">
        {!selectedId ? (
          <div className="flex flex-1 items-center justify-center p-10 text-sm text-gray-500 dark:text-gray-400">
            Select a conversation to view its history.
          </div>
        ) : (
          <>
            <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                {customerLabel(selected?.customerUserId ?? selectedId)}
              </h2>
            </div>

            <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3" style={{ maxHeight: '55vh' }}>
              {history.isLoading ? (
                <p className="text-center text-sm text-gray-500 dark:text-gray-400">Loading history…</p>
              ) : history.isError ? (
                <p className="text-center text-sm text-red-600">Failed to load message history.</p>
              ) : history.data && history.data.messages.length === 0 ? (
                <p className="text-center text-sm text-gray-500 dark:text-gray-400">No messages in this conversation yet.</p>
              ) : (
                history.data?.messages.map((m: MessageItem) => (
                  <div key={m.id} className={`flex ${m.isFromStaff ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${
                        m.isFromStaff
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100'
                      }`}
                    >
                      <p className="whitespace-pre-wrap break-words">{m.body}</p>
                      <span className={`mt-1 block text-[10px] ${m.isFromStaff ? 'text-blue-100' : 'text-gray-400 dark:text-gray-400'}`}>
                        {formatTime(m.sentAt)}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="border-t border-gray-200 p-3 dark:border-gray-700">
              {suggestionCitations.length > 0 && (
                <p className="mb-2 text-xs text-gray-400 dark:text-gray-500">
                  AI draft grounded in: {suggestionCitations.join(', ')}
                </p>
              )}
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  handleSend()
                }}
                className="flex items-center gap-2"
              >
                <button
                  type="button"
                  onClick={handleSuggestReply}
                  disabled={suggestReply.isPending}
                  aria-label="Suggest reply with AI"
                  title="Suggest a reply with AI — review and edit before sending"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700/50"
                >
                  <Sparkles className={`h-4 w-4 ${suggestReply.isPending ? 'animate-pulse' : ''}`} />
                </button>
                <input
                  type="text"
                  value={draft}
                  onChange={(e) => {
                    setDraft(e.target.value)
                    if (suggestionCitations.length > 0) setSuggestionCitations([])
                  }}
                  placeholder="Type a reply, or click the sparkle for an AI draft…"
                  aria-label="Reply"
                  className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-600 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                />
                <button
                  type="submit"
                  disabled={!draft.trim() || sendMutation.isPending}
                  aria-label="Send reply"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  <Send className="h-4 w-4" />
                </button>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export const Route = createFileRoute('/(admin)/messages/')({ component: MessagesPage })
